import type { AppSettings, Meeting, Skill, TodoItem } from "../types";

const DB_NAME = "deskflow-web";
const DB_VERSION = 2;
const SETTINGS_KEY = "deskflow.settings";
const DEMO_TODOS_SEEDED_KEY = "deskflow.demoTodosSeeded";
let databasePromise: Promise<IDBDatabase> | null = null;

export const defaultSettings: AppSettings = {
  language: "en",
  theme: "system",
  provider: {
    enabled: false,
    baseUrl: "https://api.openai.com/v1",
    model: "gpt-4.1-mini",
    apiKey: "",
  },
};

const starterSkills: Skill[] = [
  {
    id: "summarize",
    name: "Summarize a document",
    nameZh: "总结文档",
    description: "Condense long business text into the essential points.",
    descriptionZh: "将较长的商务文本浓缩为核心要点。",
    promptTemplate:
      "Summarize the following business text into concise key points:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "polish",
    name: "Polish a document",
    nameZh: "润色文档",
    description: "Make business writing clear, polished, and professional.",
    descriptionZh: "让商务文档更清晰、流畅且专业。",
    promptTemplate:
      "Polish this business document for clarity, structure, and professional tone:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "actions",
    name: "Extract action items",
    nameZh: "提取行动项",
    description: "Find tasks, owners, and dates in unstructured notes.",
    descriptionZh: "从非结构化笔记中识别任务、负责人和日期。",
    promptTemplate:
      "Extract action items, owners, and due dates from these notes. Use a clear checklist:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "report",
    name: "Create a weekly report",
    nameZh: "生成周报",
    description: "Turn raw updates into a clear weekly status report.",
    descriptionZh: "将零散进展整理为清晰的每周状态报告。",
    promptTemplate:
      "Create a weekly report with achievements, next steps, and blockers from these updates:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "translate",
    name: "Translate business text",
    nameZh: "翻译商务文本",
    description: "Translate text while preserving professional tone.",
    descriptionZh: "在保持专业语气的同时翻译文本。",
    promptTemplate:
      "Translate the following business text into the other language (English or Simplified Chinese) while preserving tone:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "agenda",
    name: "Prepare a meeting agenda",
    nameZh: "准备会议议程",
    description: "Build an agenda from goals and background notes.",
    descriptionZh: "根据目标和背景资料生成会议议程。",
    promptTemplate:
      "Create a focused meeting agenda with objectives, topics, timings, and desired decisions:\n\n{{input}}",
    builtIn: true,
    createdAt: new Date(0).toISOString(),
  },
];

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

export function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("meetings")) {
        const meetings = database.createObjectStore("meetings", {
          keyPath: "id",
        });
        meetings.createIndex("updatedAt", "updatedAt");
      }
      if (!database.objectStoreNames.contains("skills"))
        database.createObjectStore("skills", { keyPath: "id" });
      if (!database.objectStoreNames.contains("todos")) {
        const todos = database.createObjectStore("todos", { keyPath: "id" });
        todos.createIndex("dueDate", "dueDate");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      databasePromise = null;
      reject(request.error ?? new Error("Could not open local database"));
    };
  });
  return databasePromise;
}

export async function initializeStorage(): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(["skills", "todos"], "readwrite");
  const store = transaction.objectStore("skills");
  store.delete("email");
  starterSkills.forEach((skill) => store.put(skill));
  const todoStore = transaction.objectStore("todos");
  const todoCount = await requestResult(todoStore.count());
  if (
    todoCount === 0 &&
    localStorage.getItem(DEMO_TODOS_SEEDED_KEY) !== "true"
  ) {
    starterTodos().forEach((todo) => todoStore.put(todo));
  }
  localStorage.setItem(DEMO_TODOS_SEEDED_KEY, "true");
  await transactionDone(transaction);
}

function isoDate(offset: number): string {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
}

function starterTodos(): TodoItem[] {
  const createdAt = new Date().toISOString();
  return [
    {
      id: "demo-todo-brief",
      title: "Review product brief",
      owner: "Me",
      dueDate: isoDate(0),
      time: "10:00",
      kind: "todo",
      completed: false,
      notes: "Demo item stored locally in this browser.",
      source: "demo",
      sourceMeetingId: null,
      createdAt,
    },
    {
      id: "demo-schedule-sync",
      title: "Weekly team sync",
      owner: "Product team",
      dueDate: isoDate(0),
      time: "14:30",
      kind: "schedule",
      completed: false,
      notes: "Demo calendar event.",
      source: "demo",
      sourceMeetingId: null,
      createdAt,
    },
    {
      id: "demo-todo-notes",
      title: "Share meeting notes",
      owner: "Me",
      dueDate: isoDate(1),
      time: "09:00",
      kind: "todo",
      completed: true,
      notes: "Completed demo item.",
      source: "demo",
      sourceMeetingId: null,
      createdAt,
    },
  ];
}

export async function listMeetings(): Promise<Meeting[]> {
  const database = await openDatabase();
  const meetings = (await requestResult(
    database.transaction("meetings").objectStore("meetings").getAll(),
  )) as Meeting[];
  return meetings.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveMeeting(meeting: Meeting): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("meetings", "readwrite");
  transaction.objectStore("meetings").put(meeting);
  await transactionDone(transaction);
}

export async function removeMeeting(id: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("meetings", "readwrite");
  transaction.objectStore("meetings").delete(id);
  await transactionDone(transaction);
}

export async function listSkills(): Promise<Skill[]> {
  const database = await openDatabase();
  return requestResult(
    database.transaction("skills").objectStore("skills").getAll(),
  ) as Promise<Skill[]>;
}

export async function listTodos(): Promise<TodoItem[]> {
  const database = await openDatabase();
  const todos = (await requestResult(
    database.transaction("todos").objectStore("todos").getAll(),
  )) as TodoItem[];
  return todos.sort((a, b) =>
    `${a.dueDate} ${a.time}`.localeCompare(`${b.dueDate} ${b.time}`),
  );
}

export async function saveTodo(todo: TodoItem): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("todos", "readwrite");
  transaction.objectStore("todos").put(todo);
  await transactionDone(transaction);
}

export async function saveTodos(todos: TodoItem[]): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("todos", "readwrite");
  const store = transaction.objectStore("todos");
  todos.forEach((todo) => store.put(todo));
  await transactionDone(transaction);
}

export async function removeTodo(id: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("todos", "readwrite");
  transaction.objectStore("todos").delete(id);
  await transactionDone(transaction);
}

export function loadSettings(): AppSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved)
      return {
        ...defaultSettings,
        provider: { ...defaultSettings.provider },
      };
    const parsed = JSON.parse(saved) as Partial<AppSettings>;
    return {
      ...defaultSettings,
      ...parsed,
      provider: { ...defaultSettings.provider, ...parsed.provider },
      language:
        localStorage.getItem("deskflow.language") === "zh-CN" ? "zh-CN" : "en",
    };
  } catch {
    return {
      ...defaultSettings,
      provider: { ...defaultSettings.provider },
    };
  }
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  localStorage.setItem("deskflow.language", settings.language);
}

export async function exportAllData(): Promise<string> {
  const [meetings, skills, todos] = await Promise.all([
    listMeetings(),
    listSkills(),
    listTodos(),
  ]);
  return JSON.stringify(
    {
      version: 2,
      exportedAt: new Date().toISOString(),
      meetings,
      skills,
      todos,
      settings: loadSettings(),
      mockState: {
        translationHistory: localStorage.getItem("deskflow.translationHistory"),
        searchHistory: localStorage.getItem("deskflow.searchHistory"),
        skillResults: localStorage.getItem("deskflow.skillResults"),
      },
    },
    null,
    2,
  );
}

export async function deleteAllData(): Promise<void> {
  if (databasePromise) {
    const database = await databasePromise;
    database.close();
    databasePromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () =>
      reject(request.error ?? new Error("Could not delete local data"));
    request.onblocked = () =>
      reject(new Error("Close other Deskflow tabs before deleting data"));
  });
  localStorage.removeItem(SETTINGS_KEY);
  localStorage.removeItem("deskflow.language");
  localStorage.removeItem("deskflow.apiMode.v1");
  localStorage.removeItem("deskflow.apiMode.v2");
  localStorage.removeItem("deskflow.translationHistory");
  localStorage.removeItem("deskflow.searchHistory");
  localStorage.removeItem("deskflow.skillResults");
  localStorage.setItem(DEMO_TODOS_SEEDED_KEY, "true");
}
